import { captureException } from '@sentry/react';
import invariant from 'tiny-invariant';
import type { DoneInvokeEvent } from 'xstate';
import { assign } from 'xstate';

import { config as envConfig } from 'src/config';
import type { MenuItemType } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import { fetchDataWrapper } from 'src/modules/designStudio/hooks/useFetchModelData';
import { sharedOptions } from 'src/modules/designStudio/machines/sharedOptions';
import { Mode, Partner } from 'src/modules/designStudio/providers';
import { mapPartnerIdToEnum } from 'src/modules/designStudio/providers/ModeProvider';
import { DesignStudio } from 'src/shared/constants/FeatureFlag';
import { client as apolloClient } from 'src/shared/graphql';
import type {
  DesignStudioColor,
  Job,
  Maybe,
} from 'src/shared/graphql/generated';
import { DesignStudioThemeEnum } from 'src/shared/graphql/generated';
import type { GenericProductSettings } from 'src/shared/graphql/requests';
import { queries } from 'src/shared/graphql/requests';
import { requestWithRetry } from 'src/shared/helpers/request';

import {
  addThumbnailsToMenuItems,
  filterColors,
  filterMenuItems,
  filterTextures,
} from './helpers';
import type { Context, Textures } from './types';

const PRODUCTS_ENDPOINT = envConfig.PRODUCTS_URI;

const GUEST_MACHETE_FILE_LOCATION = 'guest/machete_2_10_2023.json';
const GUEST_MODEL_FILE_LOCATION = 'guest/model_2_10_2023.json';

const GUEST_MACHETE_FILE_URL = `${
  process.env.PUBLIC_PATH || '/'
}${GUEST_MACHETE_FILE_LOCATION}`;
const GUEST_MODEL_FILE_URL = `${
  process.env.PUBLIC_PATH || '/'
}${GUEST_MODEL_FILE_LOCATION}`;

const options = {
  services: {
    fetchRunningExperiments: async (ctx: Context) => {
      const splitClient = await ctx.splitClient;
      const shouldShowPartnerProducts =
        splitClient?.getTreatment(DesignStudio.DS_JH_PRODUCTS) === 'on';

      return { shouldShowPartnerProducts };
    },
    fetchMenuItems: async (ctx: Context) => {
      const themeName =
        (ctx.mode === Mode.INTERACTIVE_RENDERING &&
          ctx.partner === Partner.JAMES_HARDIE) ||
        (ctx.mode === Mode.GUEST && ctx.partner === Partner.JAMES_HARDIE) ||
        (ctx.shouldShowPartnerProducts && ctx.partner === Partner.JAMES_HARDIE)
          ? DesignStudioThemeEnum.JamesHardieWithMagnolia
          : DesignStudioThemeEnum.Default;

      const menuItems = await sharedOptions.fetchMenuItems({
        themeName,
      });

      return menuItems;
    },
    fetchPartner: async (ctx: Context) => {
      let { partner } = ctx;

      const partnerId = ctx.jobId
        ? await sharedOptions.fetchPartnerIdForJob(ctx.jobId)
        : undefined;

      if (partnerId) {
        partner = mapPartnerIdToEnum(partnerId, partner);
      }

      return { partner };
    },
    fetchProductSettingOrgId: async (ctx: Context) => {
      invariant(ctx.jobId, 'Missing jobId');
      const jobResponse = await queries.getJob(apolloClient, { id: ctx.jobId });
      return jobResponse.data.jobs.results[0];
    },
    fetchModelData: async (ctx: Context) => {
      let geometryUrl;
      let metadataUrl;
      let authToken;
      let error;
      if (ctx.mode === Mode.GUEST) {
        geometryUrl = GUEST_MODEL_FILE_URL;
        metadataUrl = GUEST_MACHETE_FILE_URL;
      } else if (ctx.mode === Mode.PREVIEW) {
        geometryUrl = GUEST_MODEL_FILE_URL;
        metadataUrl = GUEST_MACHETE_FILE_URL;
      } else {
        invariant(ctx.jobId, 'Missing jobId');
        if (ctx.mode === Mode.SHARE) {
          const { data, error: shareError } =
            await queries.jobSharedThreeDFiles(apolloClient, {
              jobId: ctx.jobId,
              shareToken: ctx.shareToken || '',
            });

          const resp = data?.jobSharedThreeDFiles ?? {};
          geometryUrl = resp.geometryUrl;
          metadataUrl = resp.metadataUrl;
          authToken = resp.authToken;
          error = shareError;
        }
      }
      const { metadata = null, geometry = null } = await fetchDataWrapper({
        geometryUrl,
        metadataUrl,
        setMetadata: () => {},
        setGeometry: () => {},
        jobId: ctx.jobId,
        mode: ctx.mode,
      });
      return { metadata, geometry, authToken, error };
    },
    fetchProductSettings: async (ctx: Context) => {
      if (ctx.productSettingsOrgId) {
        const response = await queries.getGenericProductSettings(apolloClient, {
          orgId: ctx.productSettingsOrgId.toString(),
        });

        if (!response) return null;

        const settings: GenericProductSettings = { ...response };

        Object.keys(response).forEach(key => {
          if (key && response[key] && response[key].disabled_types) {
            const mutatedTypes = response[key]?.disabled_types?.map(type =>
              type.toLowerCase(),
            );
            settings[key] = {
              ...response[key],
              disabled_types: mutatedTypes,
            };
          }
        });

        return settings;
      }
      return null;
    },
    fetchTextures: async () => {
      const textures = (await requestWithRetry(PRODUCTS_ENDPOINT)
        .then(res => res.json())
        .catch(error => {
          captureException(error);

          return [];
        })) as Textures;

      return {
        textures,
      };
    },
  },
  actions: {
    setExperiments: assign((_: Context, _event) => {
      const event = _event as DoneInvokeEvent<{
        shouldShowPartnerProducts: boolean;
      }>;

      return event.data;
    }),
    setHasUpdatedExperiments: assign((_: Context, _event) => {
      _event as DoneInvokeEvent<{}>;
      return { hasUpdatedExperiments: true };
    }),
    setMenuItems: assign((ctx: Context, _event) => {
      const event = _event as DoneInvokeEvent<{
        textures: MenuItemType[];
        colors: DesignStudioColor[];
        windowColors: DesignStudioColor[];
      }>;

      const { menuItems: menuItemsWithThumbnails } = addThumbnailsToMenuItems({
        menuItems: event.data.textures,
        textures: ctx.textures,
      });

      const {
        menuItems: filteredMenuItems,
        filterTracker: filterTrackerWithMenuItems,
      } = filterMenuItems(
        menuItemsWithThumbnails,
        ctx.filterTracker,
        ctx.productSettings,
      );

      const { filteredColors, filterTracker: filterTrackerWithColors } =
        filterColors(
          ctx.partner,
          { ...filterTrackerWithMenuItems },
          event.data.colors,
          event.data.windowColors,
          ctx.productSettings,
        );

      const { filteredTextures, filterTracker } = filterTextures(
        event.data.textures,
        filterTrackerWithColors,
        ctx.productSettings,
      );

      return {
        menuItems: filteredMenuItems,
        filteredColors,
        filteredTextures,
        filterTracker,
      };
    }),
    setPartner: assign((_: Context, _event) => {
      const event = _event as DoneInvokeEvent<{
        partner: Partner;
      }>;

      return event.data;
    }),
    updateModelData: assign((_: Context, _event: any) => {
      const event = _event as { data: { geometry: object; metadata: object } };
      return event.data;
    }),
    updateProductSettings: assign((_: Context, _event: any) => {
      const event = _event as { data: GenericProductSettings };

      return { productSettings: event.data };
    }),
    updateProductSettingsOrgId: assign((_: Context, _event: any) => {
      const { data } = _event as DoneInvokeEvent<Maybe<Job>>;

      if (!data) {
        return {};
      }

      const { orgId, captureRequest } = data;

      let productSettingsOrgId = orgId ?? undefined;

      if (captureRequest && captureRequest.requestingUser) {
        const requestingOrg = captureRequest.requestingUser.orgs.results[0];
        if (requestingOrg) {
          productSettingsOrgId = requestingOrg.id;
        }
      }

      return { productSettingsOrgId };
    }),
    assignTextures: assign((_: Context, _event: any) => {
      const event = _event as DoneInvokeEvent<{
        textures: Textures;
      }>;

      const { textures } = event.data;
      return { textures };
    }),
    logError: sharedOptions.logError,
  },
  guards: {
    isShareMode: (ctx: Context) => ctx.mode === Mode.SHARE,
    isDefaultMode: (ctx: Context) => ctx.mode === Mode.DEFAULT,
    isGuestMode: (ctx: Context) => ctx.mode === Mode.GUEST,
    isPreviewMode: (ctx: Context) => ctx.mode === Mode.PREVIEW,
    isInteractiveRenderingMode: (ctx: Context) =>
      ctx.mode === Mode.INTERACTIVE_RENDERING,
  },
};

export { options };
